import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  Alert,
  Snackbar,
  MenuItem,
  Select,
  FormControl,
  Typography,
} from '@mui/material';
import { Add, Search } from '@mui/icons-material';
import PageHeader from '../../../components/PageHeader';
import { usePermissions } from '../../../hooks/usePermissions';
import useDebounce from '../../../hooks/useDebounce';
import TemplateTable from '../components/TemplateTable';
import TemplateModalForm from '../components/TemplateModalForm';
import LoadingLogo from '../../../components/LoadingLogo';
import templateService from '../services/templateService';
import { companyService } from '../../companies';

const TemplateList = () => {
  const { hasPermission } = usePermissions();
  const [templates, setTemplates] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [helpers, setHelpers] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 2000);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadCompanies();
    loadHelpers();
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [page, debouncedSearch, selectedCompany]);

  const loadCompanies = async () => {
    try {
      const response = await companyService.getAll({ limit: 100 });
      setCompanies(response.data);
    } catch (error) {
      console.error('Error al cargar empresas:', error);
    }
  };

  const loadHelpers = async () => {
    try {
      const response = await templateService.getHelpers();
      setHelpers(response.data);
    } catch (error) {
      console.error('Error al cargar helpers:', error);
    }
  };

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await templateService.getAll({
        search: debouncedSearch,
        companyId: selectedCompany,
        page,
        limit: 10,
      });
      setTemplates(response.data);
      setPagination(response.pagination);
    } catch (error) {
      showSnackbar('Error al cargar plantillas', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedTemplate(null);
    setOpenModal(true);
  };

  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setOpenModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estas seguro de eliminar esta plantilla?')) {
      try {
        await templateService.delete(id);
        setTemplates(prev => prev.filter(t => t.id !== id));
        setPagination(prev => prev ? { ...prev, total: (prev.total || 1) - 1 } : prev);
        showSnackbar('Plantilla eliminada exitosamente', 'success');
      } catch (error) {
        showSnackbar('Error al eliminar plantilla', 'error');
      }
    }
  };

  const handleSave = async (data) => {
    try {
      if (selectedTemplate) {
        await templateService.update(selectedTemplate.id, data);
        setTemplates(prev => prev.map(t =>
          t.id === selectedTemplate.id ? { ...t, ...data } : t
        ));
        showSnackbar('Plantilla actualizada exitosamente', 'success');
      } else {
        await templateService.create(data);
        showSnackbar('Plantilla creada exitosamente', 'success');
        if (page !== 1) setPage(1);
        else loadTemplates();
      }
      setOpenModal(false);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error al guardar plantilla', 'error');
      throw error;
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box>
      <PageHeader
        title="Plantillas"
        subtitle="Documentos base para radicacion y respuesta de correspondencia."
        stat={pagination?.total || 0}
        statLabel="Plantillas"
        actions={hasPermission('template.create') && (
          <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
            Nueva Plantilla
          </Button>
        )}
      />

      <Box sx={{ bgcolor: '#f2f2f3', borderRadius: 0, p: '18px', border: '1px solid rgba(29,31,32,0.16)', mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          <Box>
            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(29,31,32,0.55)', mb: 0.5, fontWeight: 500 }}>Buscar</Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar plantillas..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(29,31,32,0.55)', mb: 0.5, fontWeight: 500 }}>Empresa</Typography>
            <FormControl fullWidth size="small">
              <Select
                value={selectedCompany}
                onChange={(e) => { setSelectedCompany(e.target.value); setPage(1); }}
                displayEmpty
              >
                <MenuItem value="">Todas</MenuItem>
                {companies.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <LoadingLogo size={120} />
        </Box>
      ) : (
        <TemplateTable
          templates={templates}
          onEdit={handleEdit}
          onDelete={handleDelete}
          page={page}
          onPageChange={setPage}
          pagination={pagination}
          canEdit={hasPermission('template.update')}
          canDelete={hasPermission('template.delete')}
        />
      )}

      <TemplateModalForm
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSave={handleSave}
        template={selectedTemplate}
        companies={companies}
        helpers={helpers}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TemplateList;
